/*
 * Splectrum Picks — <drop-hero> (sections/drop-hero.liquid).
 *
 * Minimal slide switcher over stacked slides: arrows, dots, keyboard (left /
 * right when the hero has focus), horizontal swipe, optional autoplay
 * (data-autoplay="true"; never under prefers-reduced-motion; paused on hover,
 * focus, touch and when the tab is hidden). The theme editor's block select
 * jumps to that slide.
 */
(function () {
  if (customElements.get('drop-hero')) return;

  class DropHero extends HTMLElement {
    constructor() {
      super();
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.pause = this.pause.bind(this);
      this.resume = this.resume.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
    }

    connectedCallback() {
      // On-page anchors: JSON-template sections are id'd "shopify-section-template--N__handle",
      // so a link to "#handle" is resolved by id suffix when no exact match exists.
      this.querySelectorAll('a[href^="#"]').forEach((link) => {
        link.addEventListener('click', (event) => {
          const hash = link.getAttribute('href').slice(1);
          if (!hash || document.getElementById(hash)) return;
          const target =
            document.querySelector(`[id$="__${CSS.escape(hash)}"]`) ||
            document.querySelector(`[id$="${CSS.escape(hash)}"]`);
          if (!target) return;
          event.preventDefault();
          const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
          const top = window.scrollY + target.getBoundingClientRect().top - headerH;
          window.scrollTo({ top, behavior: this.reducedMotion && this.reducedMotion.matches ? 'auto' : 'smooth' });
        });
      });
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.slides = Array.from(this.querySelectorAll('[data-dh-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-dh-goto]'));
      this.index = Math.max(
        0,
        this.slides.findIndex((s) => s.classList.contains('is-active'))
      );
      if (this.slides.length < 2) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      const prev = this.querySelector('.drop-hero__arrow--prev');
      const next = this.querySelector('.drop-hero__arrow--next');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1, true));
      if (next) next.addEventListener('click', () => this.go(this.index + 1, true));
      this.dots.forEach((dot) =>
        dot.addEventListener('click', () => this.go(parseInt(dot.dataset.dhGoto, 10), true))
      );

      this.setAttribute('tabindex', '-1');
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('pointerdown', this.onPointerDown);
      this.addEventListener('pointerup', this.onPointerUp);
      this.addEventListener('pointercancel', this.onPointerUp);

      this.autoplay = this.dataset.autoplay === 'true' && !this.reducedMotion.matches;
      this.speed = Math.max(4, parseInt(this.dataset.autoplaySpeed, 10) || 7) * 1000;
      if (this.autoplay) {
        this.addEventListener('mouseenter', this.pause);
        this.addEventListener('mouseleave', this.resume);
        this.addEventListener('focusin', this.pause);
        this.addEventListener('focusout', this.resume);
        this.addEventListener('touchstart', this.pause, { passive: true });
        document.addEventListener('visibilitychange', this.onVisibility);
        this.start();
      }

      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    go(target, userInitiated) {
      const count = this.slides.length;
      const nextIndex = ((target % count) + count) % count;
      if (nextIndex === this.index) return;
      const backwards = nextIndex < this.index;

      const current = this.slides[this.index];
      const incoming = this.slides[nextIndex];

      current.classList.remove('is-active');
      current.classList.toggle('is-leaving-back', backwards);
      current.setAttribute('aria-hidden', 'true');

      incoming.classList.remove('is-leaving-back');
      incoming.classList.add('is-active');
      incoming.removeAttribute('aria-hidden');

      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === nextIndex);
        dot.setAttribute('aria-selected', i === nextIndex ? 'true' : 'false');
      });

      this.index = nextIndex;
      if (userInitiated) this.restart();
    }

    onKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.go(this.index + 1, true);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(this.index - 1, true);
      }
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this.swipe = { x: event.clientX, y: event.clientY, t: Date.now() };
    }

    onPointerUp(event) {
      if (!this.swipe) return;
      const dx = event.clientX - this.swipe.x;
      const dy = event.clientY - this.swipe.y;
      const fast = Date.now() - this.swipe.t < 600;
      this.swipe = null;
      if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.5 && fast) {
        this.go(this.index + (dx < 0 ? 1 : -1), true);
      }
    }

    onBlockSelect(event) {
      const slide = event.target && event.target.closest ? event.target.closest('[data-dh-slide]') : null;
      if (!slide || !this.contains(slide)) return;
      this.stop();
      this.go(this.slides.indexOf(slide), false);
    }

    start() {
      if (!this.autoplay || this.timer) return;
      this.timer = setInterval(() => this.go(this.index + 1, false), this.speed);
    }

    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      if (!this.autoplay) return;
      this.stop();
      this.start();
    }

    pause() {
      this.paused = true;
      this.stop();
    }

    resume() {
      this.paused = false;
      if (!document.hidden) this.start();
    }

    onVisibility() {
      if (document.hidden) this.stop();
      else if (!this.paused) this.start();
    }
  }

  customElements.define('drop-hero', DropHero);
})();
